<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import { useUiStore } from '@/stores';
import { UserService } from '@/services';
import { toastServiceError } from '@/services/core/notification';
import { UserModel, Permission, UserDeviceType, UserDeviceModel } from '@/types';

import { DesktopComputerIcon, DeviceMobileIcon } from '@heroicons/vue/solid';
import { InformationCircleIcon } from '@heroicons/vue/solid';
import SVGDeviceExternal from '@/components/svg/SVGDeviceExternal.vue';
import SVGChromeLogo from '@/components/svg/SVGChromeLogo.vue';

const route = useRoute();
const uiStore = useUiStore();
const { t } = useI18n();

const user = ref<UserModel | null>(null);
const isLoading = ref(false);

const setLoading = (loading: boolean) => {
    isLoading.value = loading;
    uiStore.setIsLoading(loading);
};

const fetchUsers = async (id: number) => {
    setLoading(true);
    try {
        const response = await UserService.get<UserModel>(id);
        user.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const deviceCards = [
    {
        type: UserDeviceType.MOBILE,
        icon: DeviceMobileIcon,
    },
    {
        type: UserDeviceType.DESKTOP,
        icon: DesktopComputerIcon,
    },
    {
        type: UserDeviceType.WEB,
        icon: SVGChromeLogo,
    },
    {
        type: UserDeviceType.EXTERNAL,
        icon: SVGDeviceExternal,
    },
];

const devicesByType = computed<Record<UserDeviceType, UserDeviceModel | undefined>>(() => {
    const map = {} as Record<UserDeviceType, UserDeviceModel | undefined>;
    for (const d of user.value?.devices ?? []) map[d.deviceType] = d;
    return map;
});

const hasDevice = (type: UserDeviceType) => Boolean(devicesByType.value[type]);
const externalDevice = computed(() => devicesByType.value[UserDeviceType.EXTERNAL]);

const autoAttendantTooltip = computed(() => {
    return `<span class='text-base'>${t('user.tooltips.useAutoAttendant')}</span>`;
});

onMounted(() => {
    fetchUsers(Number(route.params.id));
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('user.view.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex items-center space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListUser' }" />
                    <router-link
                        v-if="$can(Permission.EDIT_USER)"
                        :to="{
                            name: 'EditUserById',
                            params: {
                                id: route.params.id,
                            },
                        }"
                        class="block hover:bg-gray-50"
                    >
                        <UiButton variant="primary" text-variant="white">
                            {{ $t('core.actions.Edit') }}
                        </UiButton>
                    </router-link>
                </div>
            </template>
        </UiPageHeader>

        <div class="space-y-4">
            <UiPanel>
                <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                        <h3 class="mb-1 font-semibold">{{ $t('user.labels.name') }}</h3>
                        <p>{{ user?.name }}</p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">{{ $t('user.labels.email') }}</h3>
                        <p>{{ user?.email }}</p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">{{ $t('user.labels.roles') }}</h3>
                        <p>
                            <component
                                v-for="(role, index) in user?.roles"
                                :key="`role-${role.id}`"
                                :is="$can(Permission.VIEW_ROLE) ? 'RouterLink' : 'span'"
                                :to="
                                    $can(Permission.VIEW_ROLE)
                                        ? { name: 'ViewRoleById', params: { id: role.id } }
                                        : undefined
                                "
                                :class="[
                                    $can(Permission.VIEW_ROLE) && 'text-primary',
                                    index !== (user?.roles?.length || 0) - 1 ? 'mr-0.5' : '',
                                ]"
                            >
                                {{ role.name
                                }}<span v-if="index !== (user?.roles?.length || 0) - 1">,</span>
                            </component>
                        </p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">{{ $t('user.labels.levels') }}</h3>
                        <p>
                            <component
                                v-for="(level, index) in user?.levels"
                                :key="`level-${level.id}`"
                                :is="$can(Permission.VIEW_LEVEL) ? 'RouterLink' : 'span'"
                                :to="
                                    $can(Permission.VIEW_LEVEL)
                                        ? { name: 'ViewLevelById', params: { id: level.id } }
                                        : undefined
                                "
                                :class="[
                                    $can(Permission.VIEW_LEVEL) && 'text-primary',
                                    index !== (user?.levels?.length || 0) - 1 ? 'mr-0.5' : '',
                                ]"
                            >
                                {{ level.name
                                }}<span v-if="index !== (user?.levels?.length || 0) - 1">,</span>
                            </component>
                        </p>
                    </div>
                    <div>
                        <h3 class="mb-1 font-semibold">
                            {{ $t('user.labels.standardLevel') }}
                        </h3>
                        <p>
                            {{ user?.standardLevel?.name }}
                        </p>
                    </div>
                    <div>
                        <h3 class="mb-1 flex items-center gap-2 font-semibold">
                            {{ $t('user.labels.useAutoAttendant') }}
                            <InformationCircleIcon
                                class="h-4 w-4 text-gray-400"
                                aria-hidden="true"
                                v-tippy="{ content: autoAttendantTooltip, allowHTML: true }"
                            />
                        </h3>
                        <p>
                            {{
                                user?.useAutoAttendant
                                    ? $t('user.filters.isActive.options.active')
                                    : $t('user.filters.isActive.options.inactive')
                            }}
                        </p>
                    </div>
                </div>
                <UiAuditStatusInfo
                    :model-value="user"
                    class="bt-1 mt-4 border-t border-gray-300 pt-2"
                />
            </UiPanel>

            <UiPanel>
                <h3 class="mb-4 text-base font-semibold text-gray-800">
                    {{ $t('user.labels.devices.title') }}
                </h3>
                <div class="relative grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-6">
                    <div
                        v-for="d in deviceCards"
                        :key="d.type"
                        :class="[
                            hasDevice(d.type)
                                ? 'bg-cyan-50 text-cyan-600'
                                : 'bg-gray-50 text-gray-400',
                            'relative flex h-40 w-40 flex-col items-center rounded shadow ring-1 ring-gray-300 focus:ring-2 focus:ring-opacity-50',
                        ]"
                    >
                        <component :is="d.icon" class="mt-6 h-20 w-20" />
                        <p class="font-medium">{{ $t('user.labels.devices.' + d.type) }}</p>

                        <!-- External device details -->
                        <div
                            v-if="d.type === UserDeviceType.EXTERNAL && externalDevice"
                            class="absolute -right-[215px] bottom-0 mt-6 flex flex-col justify-between rounded border-b-4 border-r-4 border-cyan-600 bg-gray-50 px-4 py-3 text-sm text-gray-600 shadow"
                        >
                            <h3 class="mb-1 text-base font-semibold">Configurações do PABX</h3>
                            <p>
                                <span class="font-semibold">Hostname:</span>
                                {{ externalDevice.hostname }}
                            </p>
                            <p>
                                <span class="font-semibold">Username:</span>
                                {{ externalDevice.sipUser }}
                            </p>
                            <p>
                                <span class="font-semibold">Password:</span>
                                {{ externalDevice.sipPassword }}
                            </p>
                        </div>
                    </div>
                </div>
                <div
                    class="relative mt-5 grid grid-cols-1 gap-4 md:grid-cols-6"
                    v-if="user?.devices?.length"
                >
                    <div>
                        <p class="text-base font-semibold text-gray-800">
                            {{ $t('user.labels.extension.number') }}
                        </p>
                        <p>{{ user?.extension?.number }}</p>
                    </div>
                    <div>
                        <p class="text-base font-semibold text-gray-800">
                            {{ $t('user.labels.number') }}
                        </p>
                        <pre>{{
                            user?.numbers?.map((n) => n.number).join(', ') || $t('core.random')
                        }}</pre>
                    </div>
                </div>
            </UiPanel>
        </div>
    </div>
</template>
